import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import subscriptionService from '../../services/subscription';
import type { ActiveModule, UserLicense } from '../../types/subscription';
import ModuleAccessDenied from '../ui/ModuleAccessDenied';
import LicenseRequired from '../ui/LicenseRequired';

interface LicenseGateProps {
  children: React.ReactNode;
  moduleKey: string;
  moduleName: string;
  /**
   * Optional: an override for the org-level subscription key, if it's
   * different from moduleKey (legacy — no longer needed with the typed
   * license model but kept for callers that still pass it).
   */
  requiredOrgModule?: string;
  /**
   * Optional: require a specific license tier (type_key), or any of a list.
   * If omitted, any active license for the module is accepted.
   */
  licenseType?: string | string[];
}

/**
 * LicenseGate wraps subscription-gated module pages.
 *
 * Behaviour:
 * - Organisation has no active subscription → ModuleAccessDenied
 * - Organisation has subscription AND user holds an acceptable license tier → allow
 * - Organisation has subscription BUT user has no license → LicenseRequired
 *
 * No implicit admin bypass. Under the typed-license model admin and
 * super_admin must hold a seat like anyone else. super_admins can
 * self-assign one in a click.
 */
function LicenseGate({ children, moduleKey, moduleName, requiredOrgModule, licenseType }: LicenseGateProps) {
  const { user } = useAuth();
  const [activeModules, setActiveModules] = useState<ActiveModule[]>([]);
  const [myLicenses, setMyLicenses] = useState<UserLicense[]>([]);
  const [loading, setLoading] = useState(true);

  const orgModuleKey = requiredOrgModule ?? moduleKey;

  useEffect(() => {
    if (!user?.organisation_id) {
      setLoading(false);
      return;
    }
    Promise.all([
      subscriptionService.getActiveModules(),
      subscriptionService.getMyLicenses(),
    ])
      .then(([modulesRes, licensesRes]) => {
        if (modulesRes.success && modulesRes.data) setActiveModules(modulesRes.data);
        if (licensesRes.success && licensesRes.data) setMyLicenses(licensesRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.organisation_id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const orgHasModule = subscriptionService.hasModuleAccess(activeModules, orgModuleKey);
  if (!orgHasModule) {
    return <ModuleAccessDenied moduleName={moduleName} />;
  }

  const requiredTypes = licenseType == null
    ? null
    : (Array.isArray(licenseType) ? licenseType : [licenseType]);

  const hasLicense = requiredTypes === null
    ? subscriptionService.hasLicense(myLicenses, moduleKey, user?.organisation_id)
    : subscriptionService.hasAnyLicenseType(myLicenses, moduleKey, requiredTypes, user?.organisation_id);

  if (!hasLicense) {
    return <LicenseRequired moduleName={moduleName} />;
  }

  return <>{children}</>;
}

export default LicenseGate;
